import React, { useEffect, useState, useRef } from 'react';
// hooks
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import stateConfig from '../stateConfig';
// store
import { getSections, clearQuery, clearQueries } from '../features/bills/billSlice';
import { fetchUpdatedTracks, setSession } from '../features/user/userSlice'; // Import your new action
// components
import ReactMarkdown from 'react-markdown';
import BillQueryForm from '../components/BillQueryForm';
import ThreeArrowsSVG from '../components/SVGs/ThreeArrowsSVG';
import Dropdown from '../components/Structures/Dropdown';
import MainLoader from '../components/Structures/MainLoader';
import versionsMap from '../versionsMap';
import { IoMdArrowBack } from 'react-icons/io';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import { formatBillNumber } from '../utils';
import Tabs from '../components/Structures/TabsSwitch';

const BillPage = () => {
  const { billId, state } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const searchParams = new URLSearchParams(location.search);
  const session = searchParams.get('session');

  const [isLoading, setIsLoading] = useState(true);
  const [isSectionsLoading, setIsSectionsLoading] = useState(false);
  const isQueryDisabled = state.toLowerCase() === 'tx' && billId === 'HB1';

  const [version, setVersion] = useState(stateConfig[state.toLowerCase()]?.defaultVersion || 'FIL');
  const [activeIndex, setActiveIndex] = useState(0);
  const [billTypes, setBillTypes] = useState([]);
  const [keyChanges, setKeyChanges] = useState('');
  const [showSectionSummary, setShowSectionSummary] = useState(true);
  const [isOverlay, setIsOverlay] = useState(false); // when true, BillQueryForm overlays the PDF only

  const { bill, pdf, versions, billSummary } = useSelector((state) => state.bill);
  const { auth, tracks, searchTerm } = useSelector((state) => state.user);

  const billsText = bill.map((bil) => bil.metadata?.text || bil.metadata?.summary || '').join('\n');

  const timeoutRef = useRef();
  const requestRef = useRef(0);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (session) {
      dispatch(setSession(session)); // Dispatch the action to update the session in Redux state
    }
  }, [session, dispatch]);

  useEffect(() => {
    const fetchBillData = async () => {
      const requestId = ++requestRef.current;
      setIsLoading(true);
      setIsSectionsLoading(false);

      try {
        const bootstrap = await dispatch(getSections({ billId, session, version, skipSections: true })).unwrap();
        if (requestId !== requestRef.current) return;

        setBillTypes(bootstrap.types || []);
        const resolvedVersion = bootstrap.version || version;

        if (resolvedVersion !== version) {
          setVersion(resolvedVersion);
          return;
        }

        setIsLoading(false);
        setIsSectionsLoading(true);

        const { sections, types } = await dispatch(getSections({ billId, session, version: resolvedVersion })).unwrap();
        if (requestId !== requestRef.current) return;

        setBillTypes(types || []);
        const allText = sections.map((section) => section.metadata?.text).join('\n');
        setKeyChanges(extractKeyChanges(allText));
      } catch (error) {
        console.error('Failed to fetch bill data:', error);
      } finally {
        if (requestId === requestRef.current) {
          setIsLoading(false);
          setIsSectionsLoading(false);
        }
      }
    };

    if (billId && session && auth) {
      fetchBillData();
    }
  }, [billId, session, version, auth, dispatch]);

  const handleSectionClick = (index) => {
    setActiveIndex(index); // Update active section index
  };

  const handleVersionComparison = () => {
    navigate(`../compare/${billId}`);
  };

  const extractKeyChanges = (text) => {
    const keyChangesStart = text.indexOf('Key Changes');
    const otherHighlightsStart = text.indexOf('Other Highlights');
    return keyChangesStart !== -1
      ? text.substring(keyChangesStart, otherHighlightsStart !== -1 ? otherHighlightsStart : undefined).trim()
      : '';
  };

  const formatTitle = (id) => {
    // Extract section and part numbers
    const sectionMatch = id.match(/S(\d+)/);
    const partMatch = id.match(/P(\d+)$/);
    const sectionNumber = sectionMatch ? sectionMatch[1] : '';
    const partNumber = partMatch ? ` Part ${partMatch[1]}` : '';
    if (sectionNumber === '0') {
      return { title: `Bill Intro${partNumber}`, partNumber };
    } else if (sectionNumber) {
      return { title: `Section ${sectionNumber}${partNumber}`, partNumber };
    } else {
      return { title: `Bill Summary${partNumber}`, partNumber };
    }
  };

  const openPopup = () => {
    const match = searchTerm.match(/^\d+/);
    const popup = window.open(
      `${stateConfig[state.toLowerCase()].website}htbin/json_trkpop?${match[0]}${state.toUpperCase()}${session.toUpperCase()}${formatBillNumber(billId)}`,
      'popup',
      'width=600,height=600'
    );

    const popupChecker = setInterval(() => {
      if (popup.closed) {
        clearInterval(popupChecker);
        setTimeout(() => {
          dispatch(fetchUpdatedTracks(searchTerm));
        }, 3000);
      }
    }, 1000);
  };

  const billSorter = (a, b) => {
    // Generalized regex to match the bill format including different prefixes, stages, sections, and parts
    const regex = new RegExp(`^(${billTypes.join('|')})(\\d+)(${versions.join('|')})?(S(\\d+))?(P(\\d+))?$`);

    const matchA = a.id.match(regex);
    const matchB = b.id.match(regex);

    if (!matchA || !matchB) return 0; // In case some id doesn't match the pattern

    // Compare prefixes (HB, SB, etc.)
    if (matchA[1] !== matchB[1]) {
      return matchA[1].localeCompare(matchB[1]);
    }

    // Compare numeric part of the bill
    const numberA = parseInt(matchA[2], 10);
    const numberB = parseInt(matchB[2], 10);
    if (numberA !== numberB) return numberA - numberB;

    // Compare versions using a custom order
    const versionOrder = ['FIL', 'ENG']; // Define your desired version order here
    const versionA = matchA[3] || '';
    const versionB = matchB[3] || '';
    const indexA = versionOrder.indexOf(versionA);
    const indexB = versionOrder.indexOf(versionB);
    if (indexA !== indexB) return indexA - indexB;

    // Compare sections
    const sectionA = matchA[5] ? parseInt(matchA[5], 10) : 9999; // Set large default if not present
    const sectionB = matchB[5] ? parseInt(matchB[5], 10) : 9999;
    if (sectionA !== sectionB) return sectionA - sectionB;

    // Compare parts
    const partA = matchA[7] ? parseInt(matchA[7], 10) : 0; // Default to 0 if not present
    const partB = matchB[7] ? parseInt(matchB[7], 10) : 0;
    if (partA !== partB) return partA - partB;

    return 0; // Identical or no discernible order
  };

  const renderBillRecords = () => {
    const filteredBills = bill.filter((record) => record.id.includes(version));

    // Assuming bills is an array of objects where each object has an id property that needs sorting
    const billsArray = Object.values(filteredBills).sort(billSorter);

    return (
      billsArray?.length > 0 &&
      billsArray.map((record, index) => {
        const isOpen = index === activeIndex;
        const { title, partNumber } = formatTitle(record.id);
        const sectionMatch = record.id.match(/S(\d+)/);
        const sectionNumber = sectionMatch ? sectionMatch[1] : '';
        const content = record.metadata ? record.metadata.text || record.metadata.summary || '' : '';

        let keyChanges = null;
        let otherHighlights = null;
        let overallImpact = null;

        if (content) {
          // Find the start indices of each subsection
          const keyChangesStart = content.indexOf('Key Changes');
          const otherHighlightsStart = content.indexOf('Other Highlights');
          const overallImpactStart = content.indexOf('Overall Impact');

          // Function to extract subsection content based on start and end indices
          const extractContent = (start, nextStart) =>
            start !== -1
              ? content
                  .substring(start, nextStart !== -1 ? nextStart : undefined)
                  .trim()
                  .replace(
                    /[<>[\]]|Overall Impact:\*\*|Key Changes:\*\*|Overall Impact:|Key Changes:|Overall Impact|Key Changes|\*\*$/g,
                    ''
                  )
              : null;

          // Extracting subsections based on identified indices
          keyChanges = sectionNumber !== '0' ? extractContent(keyChangesStart, overallImpactStart) : null;
          // otherHighlights = extractContent(otherHighlightsStart, overallImpactStart);
          overallImpact = extractContent(overallImpactStart, -1);
        }

        return (
          <li key={index} onClick={() => handleSectionClick(index)}>
            <h5 className="font-playfair text-2xl font-medium text-primary mb-5">{title}</h5>
            {keyChanges && (
              <>
                <h6 className="font-playfair font-medium text-[22px] text-black mb-[15px]">Key Changes</h6>
                <p className="text-lg font-normal leading-[30px] text-black/70 mb-5">
                  <ReactMarkdown>{keyChanges}</ReactMarkdown>
                </p>
              </>
            )}
            {overallImpact && (
              <>
                <h6 className="font-playfair font-medium text-[22px] text-black mb-[15px]">Overall Impact</h6>
                <p className="text-lg font-normal leading-[30px] text-black/70">
                  <ReactMarkdown>{overallImpact}</ReactMarkdown>
                </p>
              </>
            )}
          </li>
        );
      })
    );
  };

  return (
    <>
      {isLoading ? (
        <MainLoader />
      ) : (
        <>
          <div className="rounded-lg border border-border bg-card shadow-sm rounded-xl shadow-sm p-5 pb-[15px] w-full mb-[20px]">
            <div className="border-b border-border mb-5 pb-3.5 flex flex-col md:flex-row justify-between items-center w-full">
              <button className="hover:bg-darkblue-hover/15" onClick={() => navigate(`/${state}/search`)}>
                <IoMdArrowBack className="w-12 h-12 text-darkblue" />
              </button>
              <h2
                id="Office-Information"
                className="font-playfair text-[28px] text-darkblue font-medium leading-8 mx-2 w-full"
              >
                Bill: {billId}
              </h2>
              <button
                className="py-3 px-12 rounded-md shadow-sm bg-primary text-base font-normal text-white transition-all duration-200 hover:bg-lightblue-hover w-[250px]"
                onClick={openPopup}
              >
                Add to track
              </button>
              <div className="flex flex-col sm:flex-row items-center gap-[15px]"></div>
            </div>
            <div className="flex items-center gap-[15px]">
              <div className="hidden min-[1161px]:flex items-center gap-3 text-base font-medium text-black">
                Version
                <ThreeArrowsSVG />
              </div>
              <div className="flex flex-col lg:flex-row w-full items-center gap-[15px]">
                <Dropdown
                  id="Numberofitem"
                  options={versions?.map((ver) => (versionsMap[ver] ? { label: versionsMap[ver], value: ver } : ver))}
                  title={versions.length > 0 ? null : 'Loading versions...'}
                  value={version}
                  label={null}
                  onChange={(e) => {
                    setVersion(e.target.value);
                    dispatch(clearQuery({ auth }));
                    dispatch(clearQueries());
                  }}
                />
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    className="btn btn-enabled"
                    disabled={versions.length <= 1}
                    onClick={handleVersionComparison}
                  >
                    Compare Versions
                  </button>
                </div>
              </div>
            </div>
          </div>
          <>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-[30px] w-full mb-[20px] relative">
                <div className="">
                  <div className="bg-white border border-border rounded-xl shadow-sm w-full h-[75vh] md:h-[80vh] overflow-hidden relative">
                    <div className="absolute top-4 right-4 z-10">
                      <a
                        href={pdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-2 bg-white/90 hover:bg-white border border-border rounded-lg shadow-sm text-sm font-medium text-[#374151] hover:text-[#1F2937] transition-all duration-200"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                        Open in new tab
                      </a>
                    </div>
                    <iframe
                      className="w-full h-full border-0 rounded-xl"
                      id="bill-pdf"
                      src={pdf} // Ensure your state or reducer updates the pdf link accordingly
                      title="PDF Viewer"
                      frameBorder="0"
                    ></iframe>
                  </div>
                </div>
                {/* Overlayed Bill Query over the PDF (desktop only) */}
                {isOverlay && (
                  <div
                    className="hidden xl:block absolute left-0 top-0 w-full h-[75vh] md:h-[80vh] z-30 pointer-events-auto"
                    style={{ width: 'calc(50% - 15px)' }}
                  >
                    <div className="relative h-full overflow-auto bg-white border border-border rounded-lg shadow-lg">
                      {/* Hover zone on right edge to reveal arrow */}
                      <div className="absolute right-0 top-0 h-full w-8 group z-20">
                        <button
                          type="button"
                          aria-label="Move AI query back to the right"
                          className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 absolute top-1/2 -right-4 -translate-y-1/2 w-8 h-8 rounded-full bg-white border border-border shadow text-darkblue hover:bg-[#F3F4F6]"
                          onClick={() => setIsOverlay(false)}
                        >
                          <IoIosArrowForward className="w-5 h-5" />
                        </button>
                      </div>
                      <div className="p-4">
                        <BillQueryForm
                          billText={billsText}
                          version={version}
                          billId={billId}
                          keyChanges={keyChanges}
                          session={session}
                          state={state}
                          isQueryDisabled={isQueryDisabled}
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="relative">
                  {/* Slide-over control when not overlayed (left arrow on panel edge) */}
                  {!isOverlay && (
                    <div className="relative">
                      {/* Wrap the query form so arrow centers relative to its height */}
                      <div className="relative group">
                        {/* Hover zone along left edge to reveal arrow */}
                        <div className="hidden xl:block absolute left-0 top-0 h-full w-8 z-20">
                          <button
                            type="button"
                            aria-label="Move AI query over the PDF"
                            className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 absolute top-1/2 -left-4 -translate-y-1/2 w-8 h-8 rounded-full bg-white border border-border shadow-sm text-darkblue hover:bg-[#F3F4F6]"
                            onClick={() => setIsOverlay(true)}
                          >
                            <IoIosArrowBack className="w-5 h-5" />
                          </button>
                        </div>
                        <BillQueryForm
                          billText={billsText}
                          version={version}
                          billId={billId}
                          keyChanges={keyChanges}
                          session={session}
                          state={state}
                          isQueryDisabled={isQueryDisabled}
                        />
                      </div>
                    </div>
                  )}
                  <div className="rounded-lg border border-border bg-card shadow-sm rounded-xl shadow-sm p-5 pt-[15px]">
                    <div className="border-b border-border mb-5 pb-2.5">
                      <h2 className="font-playfair text-[28px] text-darkblue font-medium leading-8">
                        AI {showSectionSummary ? 'Section' : 'Bill'} Summary
                      </h2>
                    </div>
                    <Tabs
                      options={[
                        { label: 'Bill Summary', value: false },
                        { label: 'Section Summary', value: true },
                      ]}
                      isActive={showSectionSummary}
                      onSwitch={() => setShowSectionSummary(!showSectionSummary)}
                    />
                    {showSectionSummary ? (
                      isSectionsLoading ? (
                        <MainLoader />
                      ) : (
                        <ul className="flex flex-col gap-[30px]">{renderBillRecords()}</ul>
                      )
                    ) : (
                      <div>
                        <h6 className="font-playfair font-medium text-[22px] text-black mb-5">Key Changes</h6>
                        <p className="text-lg font-normal leading-[30px] text-black/70 mb-5">
                          <ReactMarkdown>{billSummary?.text.replace('Key Changes', '') || ''}</ReactMarkdown>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="insufficient">
                The responses on this website are generated by artificial intelligence tools and do not reflect the
                views or opinions of our company. Please verify information independently.
              </div>
          </>
        </>
      )}
    </>
  );
};

export default BillPage;
